// The shopper's basket (browser only). Every change goes to bristol, which
// re-prices it (WEB_V2_BUILD.md SH1); the store only keeps what bristol
// last said. Nothing is reserved and there's no checkout yet.
import {defineStore} from "pinia";
import {shopApi, ShopApiError} from "./api.js";

export const useBasketStore = defineStore("shopBasket", {
    state: () => ({
        basket: null,       // bristol's basket object
        loaded: false,
        busy: false,
        error: "",
        drawerOpen: false,
        toast: null,        // {name, photo, count} after an add (phones)
        suggestion: null,
        skipped: [],        // add-on link ids the last add left out (sold out a moment ago)
    }),
    getters: {
        count: (s) => s.basket?.count || 0,
        lines: (s) => s.basket?.lines || [],
    },
    actions: {
        async load() {
            if (typeof window === "undefined") return;
            try {
                this.basket = await shopApi("/basket");
            } catch (e) {
                this.error = e instanceof ShopApiError && e.status === 404 ? "" : (e.message || "");
            }
            this.loaded = true;
        },
        async run(fn) {
            this.busy = true;
            this.error = "";
            try {
                this.basket = await fn();
                return true;
            } catch (e) {
                this.error = e.message || "Something went wrong";
                return false;
            } finally {
                this.busy = false;
            }
        },
        // addonIds: the add-on link ids ticked on the product page (bristol adds
        // one linked line each); `addonsSkipped` in the answer names the ones
        // it could not add, and the product still went in.
        async add(variantId, quantity = 1, {name = "", photo = "", toast = true, addonIds = []} = {}) {
            const body = {variantId, quantity};
            if (addonIds.length) body.addonIds = addonIds;
            this.skipped = [];
            const ok = await this.run(() => shopApi("/basket/lines", {method: "POST", body}));
            if (ok) {
                this.skipped = Array.isArray(this.basket?.addonsSkipped) ? [...this.basket.addonsSkipped] : [];
                if (toast) this.toast = {name, photo, count: this.count, at: Date.now()};
                this.loadSuggestion();
            }
            return ok;
        },
        setQuantity(lineId, quantity) {
            return this.run(() => shopApi(`/basket/lines/${lineId}`, {method: "PATCH", body: {quantity}}));
        },
        remove(lineId) {
            return this.run(() => shopApi(`/basket/lines/${lineId}`, {method: "DELETE"}));
        },
        setNote(note) {
            return this.run(() => shopApi("/basket/note", {method: "PUT", body: {note}}));
        },
        async loadSuggestion() {
            const ids = (this.basket?.lines || []).map((l) => l.productId);
            if (!ids.length) { this.suggestion = null; return; }
            try {
                const r = await shopApi(`/suggest?exclude=${ids.join(",")}`);
                this.suggestion = r.product || null;
            } catch (e) {
                this.suggestion = null;
            }
        },
        openDrawer() { this.drawerOpen = true; this.loadSuggestion(); },
        closeDrawer() { this.drawerOpen = false; },
    },
});
